import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// ==================== Initial State ====================
const initialState = {
  progressNotes: [],
  currentProgressNote: null,
  isLoading: false,
  error: null,
};

// ==================== Async Thunks ====================

// Fetch all progress notes
export const fetchProgressNotes = createAsyncThunk(
  'progressNotes/fetchProgressNotes',
  async (patientId = null, { rejectWithValue }) => {
    try {
      const url = patientId
        ? `/progress-notes/${patientId}`
        : '/progress-notes';

      const response = await api.get(url);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch Progress Notes'
      );
    }
  }
);

// Fetch single progress note
export const fetchProgressNote = createAsyncThunk(
  'progressNotes/fetchProgressNote',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/progress-notes/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch Progress Note'
      );
    }
  }
);

// Create new progress note
export const createProgressNote = createAsyncThunk(
  'progressNotes/createProgressNote',
  async (noteData, { rejectWithValue }) => {
    try {
      const response = await api.post('/progress-notes', noteData);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) return rejectWithValue(error.response.data.errors);
      return rejectWithValue(
        error.response?.data?.error || 'Failed to create Progress Note'
      );
    }
  }
);

// Update progress note
export const updateProgressNote = createAsyncThunk(
  'progressNotes/updateProgressNote',
  async ({ case_id, noteData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/progress-notes/case/${case_id}`, noteData, );
      return response.data.data ?? response.data;
    } catch (error) {
      if (error.response?.status === 422) return rejectWithValue(error.response.data.errors);
      return rejectWithValue(
        error.response?.data?.error || 'Failed to update Progress Note'
      );
    }
  }
);

// Delete progress note
export const deleteProgressNote = createAsyncThunk(
  'progressNotes/deleteProgressNote',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/progress-notes/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to delete Progress Note'
      );
    }
  }
);

// ==================== Slice ====================
const progressNotesSlice = createSlice({
  name: 'progressNotes',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentProgressNote: (state, action) => {
      state.currentProgressNote = action.payload;
    },
    clearCurrentProgressNote: (state) => {
      state.currentProgressNote = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // FETCH ALL
      .addCase(fetchProgressNotes.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProgressNotes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.progressNotes = action.payload;
      })
      .addCase(fetchProgressNotes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // FETCH SINGLE
      .addCase(fetchProgressNote.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProgressNote.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentProgressNote = action.payload;
      })
      .addCase(fetchProgressNote.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // CREATE
      .addCase(createProgressNote.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createProgressNote.fulfilled, (state, action) => {
        state.isLoading = false;
        state.progressNotes.unshift(action.payload);
      })
      .addCase(createProgressNote.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // UPDATE
      .addCase(updateProgressNote.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateProgressNote.fulfilled, (state, action) => {
        state.isLoading = false;
        const updated = action.payload.data ?? action.payload;

        const index = state.progressNotes.findIndex((d) => d.id === updated.id);
        if (index !== -1) state.progressNotes[index] = updated;

        if (state.currentProgressNote?.id === updated.id) {
          state.currentProgressNote = updated;
        }
      })
      .addCase(updateProgressNote.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // DELETE
      .addCase(deleteProgressNote.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteProgressNote.fulfilled, (state, action) => {
        state.isLoading = false;
        state.progressNotes = state.progressNotes.filter(
          (d) => d.id !== action.payload
        );

        if (state.currentProgressNote?.id === action.payload) {
          state.currentProgressNote = null;
        }
      })
      .addCase(deleteProgressNote.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, setCurrentProgressNote, clearCurrentProgressNote } =
  progressNotesSlice.actions;
export default progressNotesSlice.reducer;
